import { lazy, Suspense } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import CreateMatterModal from '../components/CreateMatterModal';
import CreateContactModal from '../components/CreateContactModal';
import HelpLauncher from '../components/HelpLauncher/HelpLauncher';
import QuickNoteModal from '../components/QuickNoteModal';
import PunchClockModal from '../components/PunchClockModal';
import TimerModal from '../components/TimerModal';
import { invalidateTimeEntrySaveCaches } from '../utils/billingMoveCache';

const QuickIntakeWizard = lazy(() => import('../components/QuickIntakeWizard'));

interface AppLayoutOverlaysProps {
  showCreateContact: boolean;
  showCreateMatter: boolean;
  showPunchModal: boolean;
  showQuickIntake: boolean;
  showQuickNote: boolean;
  showQuickTimeEntry?: boolean;
  activeMatterId?: number;
  onCloseCreateContact: () => void;
  onCloseCreateMatter: () => void;
  onClosePunchModal: () => void;
  onCloseQuickIntake: () => void;
  onCloseQuickNote: () => void;
  onCloseQuickTimeEntry?: () => void;
  suppressFloatingActions?: boolean;
}

export function AppLayoutOverlays({
  showCreateContact,
  showCreateMatter,
  showPunchModal,
  showQuickIntake,
  showQuickNote,
  showQuickTimeEntry = false,
  activeMatterId,
  onCloseCreateContact,
  onCloseCreateMatter,
  onClosePunchModal,
  onCloseQuickIntake,
  onCloseQuickNote,
  onCloseQuickTimeEntry,
  suppressFloatingActions = false,
}: AppLayoutOverlaysProps) {
  const queryClient = useQueryClient();
  return (
    <div data-testid="app-layout-overlays" data-active-matter-id={activeMatterId} style={{ display: 'contents' }}>
      {showCreateMatter && (
        <CreateMatterModal onClose={onCloseCreateMatter} />
      )}
      {showCreateContact && (
        <CreateContactModal onClose={onCloseCreateContact} />
      )}
      {showQuickIntake && (
        <Suspense fallback={null}>
          <QuickIntakeWizard onClose={onCloseQuickIntake} />
        </Suspense>
      )}
      <QuickNoteModal open={showQuickNote} onClose={onCloseQuickNote} />

      {showPunchModal && <PunchClockModal onClose={onClosePunchModal} />}

      {showQuickTimeEntry && onCloseQuickTimeEntry && (
        <TimerModal
          mode="manual"
          defaultMatterId={activeMatterId}
          onClose={onCloseQuickTimeEntry}
          onSuccess={(entry) => {
            onCloseQuickTimeEntry();
            invalidateTimeEntrySaveCaches(queryClient, activeMatterId ?? entry.matter_id, entry.matter_id);
          }}
        />
      )}

      {!suppressFloatingActions && (
        <>
          <HelpLauncher />
        </>
      )}
    </div>
  );
}
